/**
 * products (DEC-026, 2026-05-17 quick 260517-d8h).
 *
 * Tenant-scoped catalog of products a hauler can capture per tank. Mirrors
 * the canonical tenant-scoped RLS pattern used by company_sites.
 *
 * RLS: standard predicate
 *   tenant_id = current_setting('app.current_tenant_id')::uuid
 * applied via pgPolicy 'products_tenant_isolation'.
 *
 * Uniqueness: (tenant_id, code) UNIQUE — `code` is the stable machine
 * identifier (e.g. 'PRODUCED_WATER', 'OIL_SKIM'); `name` is the mutable
 * display label. Mirrors job_types.code vs job_types.label.
 *
 * Used by:
 *   - jobs.default_product_id (FK from job_types) — cascade default
 *   - haul_capture_tanks.product_id (FK) — per-tank override; snapshot at
 *     INSERT (the API resolves the default at write-time so historical
 *     captures stay stable if a job_type default is later changed)
 *
 * Locked contracts (future phases must NOT change without DEC-NNN):
 *   - UNIQUE (tenant_id, code) — code is the cross-system stable id.
 *   - RLS predicate is the standard tenant_id form (no operator-id widening,
 *     no cross-tenant SECURITY DEFINER read function in this DEC).
 *
 * Sources:
 *   - DEC-026 (gsd/DECISIONS_LOG.md)
 *   - master spec §13 (HAUL CAPTURE — volume sources / product taxonomy)
 *   - sites.ts (canonical tenant-scoped RLS table pattern)
 */

import {
  boolean,
  check,
  foreignKey,
  index,
  integer,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants } from './users.js';
import { importBatches } from './imports.js';

export const products = pgTable(
  'products',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    name: text('name').notNull(),
    code: text('code').notNull(),
    unitOfMeasure: text('unit_of_measure').notNull(),
    // D-04 (Phase 12.1) — flat per-unit rate for LINE_ITEM captures. Nullable: no
    // admin UI exists yet to set it (OPEN-015, deferred); a product can exist
    // unrated. Phase 12.1 follow-up: an unrated product NO LONGER rejects the
    // capture — capture-write.ts snapshots rate_cents_at_capture NULL and the
    // downstream billable_event carries rate NULL (the "needs pricing" signal;
    // billable_amount computes to $0 via COALESCE, never a silent priced bill).
    // PRODUCT_RATE_NOT_CONFIGURED remains registered in the error catalog but is no
    // longer thrown. No per-customer override in 12.1 (OPEN-013, deferred) — the
    // catalog is the single rate source of truth for line items.
    defaultUnitRateCents: integer('default_unit_rate_cents'),
    isActive: boolean('is_active').notNull().default(true),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // Task 20.5 provenance marker — set only by the import commit engine (20.6).
    // NULL means "not created by an import", which is every row today.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    // Task 20.5 — per-batch reconciliation and undo. Partial: almost no row
    // is import-created, so a full index would be mostly NULLs.
    index('products_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // Task 20.6a / DEC-265 — composite so a row cannot claim ANOTHER tenant's
    // batch. Declared here rather than as a column .references() because the
    // reference spans two columns. One detail Drizzle cannot express: the
    // migration uses ON DELETE SET NULL ("import_batch_id"), the Postgres 15
    // column-scoped form, because a bare SET NULL would also null tenant_id
    // (NOT NULL) and make deleting a batch fail. 0107 is authoritative.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'products_import_batch_tenant_fk',
    }).onDelete('set null'),
    uniqueIndex('products_tenant_code_uq').on(t.tenantId, t.code),
    index('products_tenant_active_idx').on(t.tenantId, t.isActive),
    check(
      'products_default_unit_rate_nonneg_check',
      sql`default_unit_rate_cents IS NULL OR default_unit_rate_cents >= 0`,
    ),
    pgPolicy('products_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type Product = typeof products.$inferSelect;
export type NewProduct = typeof products.$inferInsert;
export const productSelectSchema = createSelectSchema(products);
export const productInsertSchema = createInsertSchema(products);
